import { COMPETITION_GENDER_OPTIONS } from '@kit/shared/gender';

import type { ApplicationStructure } from '../types/audition.types';

/**
 * Canonical structure-driven competition application form.
 *
 * Encodes the SAME fields the legacy hardcoded `CompetitionApplicationWizard`
 * collected (parity map: competition apply wizard), expressed as an
 * `ApplicationStructure` so all competitions render through the unified
 * structure-driven form (StepperFormRenderer) instead of the bespoke wizard.
 *
 * Data-quality intent: profile-bound fields (`source: 'profile'`) prefill from
 * the dancer's profile and fill-if-null backfill on submit; the `experience`
 * field embeds the live experience editor — so every application refreshes the
 * dancer's profile data. The enforced booklet fields (firstname … current
 * ballet school) are also injected/locked at save+read by `ensureEnforcedFields`
 * — they're included here in their natural step so they aren't appended at the
 * end.
 *
 * The `categorySection` heading declares a dedicated Category step: the
 * positional category fields (`age_category`, `competition_styles`) are injected
 * into it at read-time by `ensureEnforcedFields` so the first step stays
 * profile-only and category selection is its own step.
 *
 * Steps mirror the wizard: Profile · Category · Contact · Experience · CV ·
 * Photos · Videos.
 */
export const COMPETITION_APPLICATION_STRUCTURE: ApplicationStructure = {
  version: 1,
  fields: [
    // ── Step 1: Profile ────────────────────────────────────────────────
    { key: 'profileSection', type: 'heading', label: 'Profile', sortOrder: 0 },
    {
      key: 'firstname',
      type: 'text',
      label: 'First Name',
      source: 'profile',
      locked: true,
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 1,
    },
    {
      key: 'lastname',
      type: 'text',
      label: 'Last Name',
      source: 'profile',
      locked: true,
      validation: { required: true, maxLength: 100 },
      width: 'half',
      sortOrder: 2,
    },
    {
      key: 'birthdate',
      type: 'date',
      label: 'Date of Birth',
      source: 'profile',
      locked: true,
      validation: { required: true },
      width: 'half',
      sortOrder: 3,
    },
    {
      key: 'gender',
      type: 'radio',
      label: 'Gender',
      source: 'profile',
      locked: true,
      // Binary set for competition categories (rendered via the segmented
      // GenderSelector), from the canonical registry. The `gender_type` enum
      // still carries `nonBinary` for existing data; it is just not offered for
      // new competition entries.
      options: COMPETITION_GENDER_OPTIONS,
      width: 'half',
      sortOrder: 4,
    },
    {
      key: 'height',
      type: 'number',
      label: 'Height (cm)',
      source: 'profile',
      // BAL-372: height is mandatory. The structured renderer wires
      // `validation.required` into a real RHF rule for number fields, so this
      // both shows the `*` marker and blocks Next/Submit on an empty value.
      validation: { required: true, min: 60, max: 250 },
      width: 'half',
      sortOrder: 5,
    },
    {
      key: 'nationalities',
      type: 'multiselect',
      label: 'Nationalities',
      source: 'profile',
      widget: 'country',
      validation: { required: true },
      width: 'half',
      sortOrder: 6,
    },
    {
      key: 'residenceCountry',
      type: 'select',
      label: 'Country of Residence',
      source: 'profile',
      locked: true,
      widget: 'country',
      validation: { required: true },
      width: 'half',
      sortOrder: 7,
    },

    // ── Step 2: Category ───────────────────────────────────────────────
    // The `age_category` + `competition_styles` fields are injected here at
    // read-time by `ensureEnforcedFields` (they aren't baked in so a competition
    // that hasn't been re-enforced still resolves them). This heading exists so
    // the Profile step stays profile-only and category selection is its own step.
    {
      key: 'categorySection',
      type: 'heading',
      label: 'Category',
      sortOrder: 8,
    },

    // ── Step 3: Contact ────────────────────────────────────────────────
    { key: 'contactSection', type: 'heading', label: 'Contact', sortOrder: 9 },
    {
      key: 'contactEmail',
      type: 'email',
      label: 'Contact Email',
      source: 'profile',
      locked: true,
      validation: { required: true },
      width: 'full',
      sortOrder: 10,
    },
    {
      key: 'parentContactEmail',
      type: 'email',
      label: 'Parent / Guardian Email',
      description: 'For applicants under 18.',
      locked: true,
      width: 'half',
      sortOrder: 11,
    },
    {
      key: 'teacherContactEmail',
      type: 'email',
      label: 'Teacher / School Email',
      locked: true,
      width: 'half',
      sortOrder: 12,
    },
    {
      key: 'currentBalletSchool',
      type: 'text',
      label: 'Current Ballet School',
      locked: true,
      width: 'half',
      sortOrder: 13,
    },
    {
      key: 'currentBalletSchoolDirector',
      type: 'text',
      label: 'School Director',
      width: 'half',
      sortOrder: 14,
    },

    // ── Step 4: Experience (data-quality touchpoint) ───────────────────
    {
      key: 'experienceSection',
      type: 'heading',
      label: 'Experience',
      sortOrder: 15,
    },
    {
      key: 'experience',
      type: 'experience',
      label: 'Your Experience',
      description:
        'Add or update your performance, training, and award history. This updates your Ballee profile.',
      width: 'full',
      sortOrder: 16,
    },

    // ── Step 5: CV ─────────────────────────────────────────────────────
    { key: 'cvSection', type: 'heading', label: 'CV', sortOrder: 17 },
    {
      key: 'cv',
      type: 'file',
      label: 'CV / Resume',
      description: 'PDF, up to 10 MB.',
      validation: { fileTypes: ['application/pdf'] },
      width: 'full',
      sortOrder: 18,
    },

    // ── Step 6: Photos ─────────────────────────────────────────────────
    { key: 'photosSection', type: 'heading', label: 'Photos', sortOrder: 19 },
    {
      key: 'headshot',
      type: 'file',
      label: 'Headshot',
      validation: {
        fileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      },
      width: 'half',
      sortOrder: 20,
    },
    {
      key: 'photos',
      type: 'file',
      label: 'Dance Photos',
      allowMultiple: true,
      maxFiles: 10,
      validation: {
        fileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
      },
      width: 'full',
      sortOrder: 21,
    },

    // ── Step 7: Videos ─────────────────────────────────────────────────
    { key: 'videosSection', type: 'heading', label: 'Videos', sortOrder: 22 },
    {
      key: 'videos',
      type: 'videoCategories',
      label: 'Performance Videos',
      description:
        'Upload files or paste links to add media. These materials will be visible to the competition partners.',
      validation: {
        fileTypes: ['video/mp4', 'video/quicktime', 'video/webm'],
      },
      options: [
        { value: 'ballet_class', label: 'Ballet Class' },
        { value: 'variation', label: 'Variation' },
        { value: 'contemporary', label: 'Contemporary' },
        { value: 'pas_de_deux', label: 'Pas de Deux' },
      ],
      width: 'full',
      sortOrder: 23,
    },
  ],
  settings: {
    showProgressIndicator: true,
    allowSaveDraft: true,
  },
};

/** Returns a fresh copy of the canonical competition application structure. */
export function getCompetitionApplicationStructure(): ApplicationStructure {
  return structuredClone(COMPETITION_APPLICATION_STRUCTURE);
}
